import moment from 'moment-timezone';

import {
  type DateRange,
  Location,
  MediaRequestStatusFilterV2,
  MediaStatus,
  MediaType,
  SafetyReviewStatus,
  Severity,
  SuggestedOccurrence,
  type Trip,
  type UUID,
  type Vehicle,
  type VehicleGroup,
} from '@cobliteam/common-types';

import { useVehicle, useVehicleGroup } from '@cobliteam/hooks';

import type { MediaRequestData } from '../types/MediaRequest';

export const DEFAULT_MEDIA_REQUEST_STATUS = [MediaRequestStatusFilterV2.PENDING, MediaRequestStatusFilterV2.COMPLETED];

export const getMediaSuggestedOccurrencesLabel = (suggestedOccurrence: SuggestedOccurrence) => {
  switch (suggestedOccurrence) {
    case SuggestedOccurrence.IS_DAYTIME:
      return 'Dia';
    case SuggestedOccurrence.IS_AFTERNOON:
      return 'Tarde';
    case SuggestedOccurrence.IS_NIGHT:
      return 'Noite';
    case SuggestedOccurrence.IS_SUNNY:
      return 'Ensolarado';
    case SuggestedOccurrence.IS_CLOUDY:
      return 'Nublado';
    case SuggestedOccurrence.IS_RAINING:
      return 'Chovendo';
    case SuggestedOccurrence.IS_HEAVY_TRAFFIC:
      return 'Trânsito intenso';
    case SuggestedOccurrence.HAS_PASSENGER:
      return 'Há passageiro';
    case SuggestedOccurrence.IS_DIRT_ROAD:
      return 'Estrada de terra';
    case SuggestedOccurrence.HAS_NOT_SEATBELT:
      return 'Sem cinto de segurança';
    case SuggestedOccurrence.IS_USING_PHONE:
      return 'Usando o celular';
    default:
      return 'Desconhecido';
  }
};

export enum MediaTableType {
  EVENT_MEDIA = 'EVENT_MEDIA',
  FOOTAGE_MEDIA = 'FOOTAGE_MEDIA',
}

export const getMediaStatusBasedOnReviewStatus = (reviewStatus?: SafetyReviewStatus) => {
  switch (reviewStatus) {
    case SafetyReviewStatus.RELEVANT:
      return MediaStatus.RELEVANT;
    case SafetyReviewStatus.DISMISSED:
      return MediaStatus.DISMISSED;
    default:
      return undefined;
  }
};

export enum EventTableType {
  DASHBOARD_EVENT_MEDIA = 'DASHBOARD_EVENT_MEDIA',
  EVENT_MEDIA = 'EVENT_MEDIA',
  FAVORITE_EVENT_MEDIA = 'FAVORITE_EVENT_MEDIA',
}

export enum RequestTableType {
  REQUEST_MEDIA = 'REQUEST_MEDIA',
  AUTOMATIC_REQUEST_MEDIA = 'AUTOMATIC_REQUEST_MEDIA',
  FAVORITE_REQUEST_MEDIA = 'FAVORITE_REQUEST_MEDIA',
}

export enum TripImageHistoryTableType {
  ALL_TRIPS = 'ALL_TRIPS',
  PENDING_NOMINATE_TRIPS = 'PENDING_NOMINATE_TRIPS',
}

export const getMediaRequestTab = (currentRoute: string) => {
  switch (currentRoute) {
    case Location.CAMERA_REQUESTS_FAVORITES:
      return RequestTableType.FAVORITE_REQUEST_MEDIA;
    case Location.CAMERA_REQUESTS_AUTOMATIC:
      return RequestTableType.AUTOMATIC_REQUEST_MEDIA;
    default:
      return RequestTableType.REQUEST_MEDIA;
  }
};

export const getVehiclesFromSelectedVehicleGroups = (vehicleGroups: VehicleGroup[], vehicles: Vehicle[], wantedVehicleGroups: UUID[]): Vehicle[] => {
  const vehiclesFromSelectedGroups: Vehicle[] = [];

  wantedVehicleGroups.forEach((vehicleGroupId) => {
    const vehicleGroup = vehicleGroups.find(({ id }) => id === vehicleGroupId);
    const vehiclesFromGroup = vehicleGroup?.vehicleIds || [];

    vehiclesFromGroup.forEach((vehicleId) => {
      const vehicle = vehicles.find(({ id }) => id === vehicleId);

      if (vehicle && !vehiclesFromSelectedGroups.includes(vehicle)) {
        vehiclesFromSelectedGroups.push(vehicle);
      }
    });
  });

  return vehiclesFromSelectedGroups;
};
export const getSeverityTagLabel = (severity: Severity, count?: number) => {
  switch (severity) {
    case Severity.VERY_SEVERE:
      return count !== undefined ? `${count} Gravíssima` : 'Gravíssima';
    case Severity.SEVERE:
      return count !== undefined ? `${count} Grave` : 'Grave';
    case Severity.MEDIUM:
      return count !== undefined ? `${count} Média` : 'Média';
    default:
      return count !== undefined ? `${count} Gravíssima` : 'Gravíssima';
  }
};

export enum Months {
  Janeiro = 0,
  Fevereiro = 1,
  Março = 2,
  Abril = 3,
  Maio = 4,
  Junho = 5,
  Julho = 6,
  Agosto = 7,
  Setembro = 8,
  Outubro = 9,
  Novembro = 10,
  Dezembro = 11,
}

export const DEFAULT_MEDIA_REQUEST_DATA: MediaRequestData = {
  hasNotificationEnabled: false,
};

export const MEDIA_DEFAULT_DURATION_IN_MILLIS = 60000;

export const DEFAULT_MEDIA_REQUEST_FOOTAGE_AVAILABILITY = [true, false];

export const MEDIA_REQUEST_MODAL_BASE_PERIOD = {
  before: moment().subtract(1, 'years').startOf('day').toDate(),
  after: moment().endOf('day').toDate(),
};

export const MEDIA_REQUEST_MODAL_TRIPS_LIMIT = {
  min: 0,
  max: 10000,
};

export const MEDIA_REQUEST_MODAL_MEDIAS_LIMIT = {
  min: 0,
  max: 10000,
};

export const formatTimeHourAndMinute = (timestamp: number) => moment(timestamp).format('HH:mm');

export const getRequestPeriod = (mediaRequestData?: MediaRequestData, selectedTrip?: Trip): DateRange | undefined => {
  const { mediaType, requestMinute, requestDate } = mediaRequestData as MediaRequestData;

  if (!selectedTrip) {
    return undefined;
  }

  if (mediaType === MediaType.TIMELAPSE) {
    return {
      start: selectedTrip.startTimestamp,
      end: selectedTrip.endTimestamp,
    };
  }

  const startPeriodString = formatTimeHourAndMinute(selectedTrip.startTimestamp);
  const endPeriodString = formatTimeHourAndMinute(selectedTrip.endTimestamp);

  if (requestMinute === startPeriodString) {
    return {
      start: selectedTrip.startTimestamp,
      end: selectedTrip.startTimestamp + MEDIA_DEFAULT_DURATION_IN_MILLIS,
    };
  }

  if (requestMinute === endPeriodString) {
    return {
      start: selectedTrip.endTimestamp - MEDIA_DEFAULT_DURATION_IN_MILLIS,
      end: selectedTrip.endTimestamp,
    };
  }

  const [hours, minutes] = requestMinute!.split(':');
  const hourAndMinuteStartTimestamp = moment(requestDate)
    .set({
      hour: Number.parseInt(hours, 10),
      minute: Number.parseInt(minutes, 10),
      second: 0,
      millisecond: 0,
    })
    .valueOf();

  return {
    start: hourAndMinuteStartTimestamp,
    end: hourAndMinuteStartTimestamp + MEDIA_DEFAULT_DURATION_IN_MILLIS,
  };
};

export const useGetVehicles = () => {
  const { getVehicles } = useVehicle();
  const { data: vehicles = [] } = getVehicles({
    enabled: true,
    staleTime: 300000,
  });

  return vehicles;
};

export const useGetVehicleGroups = () => {
  const { getVehicleGroups } = useVehicleGroup();
  const { data: vehicleGroups = [] } = getVehicleGroups({
    staleTime: 300000,
  });

  return vehicleGroups;
};

export const mediaTypeLabels: { [key in MediaType]: string } = {
  [MediaType.EVENT_VIDEO]: 'Vídeo de evento',
  [MediaType.FOOTAGE_VIDEO]: 'Vídeo de um trecho',
  [MediaType.TIMELAPSE]: 'Resumo do trajeto',
};

export const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noreferrer');
};
